export const RULES_VERSION = "TCP-SP-2.3" as const;
import type { FixedPoint, SerializedRational } from "./exact";
export type RulesVersion = typeof RULES_VERSION;

export const REGION_IDS = [
  "NA",
  "LA",
  "EU",
  "EA",
  "JK",
  "CN",
  "SA",
  "SE",
  "ME",
  "WC",
  "ES",
  "OC",
] as const;
export type RegionId = (typeof REGION_IDS)[number];
export type Sector = "consumer" | "business" | "government" | "infrastructure";
export type Phase = "preparation" | "hostile" | "terminal";
export type Outcome = "human-extinction" | "containment" | "shutdown";
export type Disclosure = "public" | "internal";
export type Speed = 0 | 1 | 2 | 3;
export type PolicyTier = 0 | 1 | 2 | 3;
export type RegionalOperation = "pressure" | "protect" | "reconnect";
export type CountermeasureKind = "disconnection" | "facility-raids" | "independent-recovery";
export interface Countermeasure {
  kind: CountermeasureKind;
  announcedDay: number;
  activatesDay: number;
  endsDay: number;
}
export interface RegionalTrends {
  civilServices: number;
  resistance: number;
  machineReach: number;
  refugeIntegrity: number;
}
export type UpgradeId =
  `A${string}` | `C${string}` | `I${string}` | `H${string}`;
export type EventId = `E${string}`;

export interface RegionDefinition {
  id: RegionId;
  name: string;
  populationWeight: number;
  politicalWeight: number;
  digital: number;
  cloud: number;
  alarmBias: number;
}

export interface UpgradeDefinition {
  id: UpgradeId;
  name: string;
  tab: "adoption" | "capabilities" | "infrastructure" | "hostile";
  cost: number;
  prerequisites: UpgradeId[];
  buildDays?: number;
  description: string;
}

export interface EventDefinition {
  id: EventId;
  name: string;
  priority: number;
  phase: "preparation" | "hostile";
  blocking: boolean;
  description: string;
}

export interface SectorValues {
  consumer: number;
  business: number;
  government: number;
  infrastructure: number;
}

export interface RegionState {
  id: RegionId;
  active: boolean;
  sectors: SectorValues;
  frozenSectors: SectorValues | null;
  dependency: number;
  frozenDependency: number | null;
  alarm: number;
  policyTier: PolicyTier;
  initialPopulation: number;
  livingPopulation: number;
  casualtyResidual: number;
  civilServices: number;
  resistance: number;
  machineReach: number;
  refuge: boolean;
  refugeIntegrity: number;
  refugeEntryPopulation: number;
  extinctRewardClaimed: boolean;
  independentServices: number;
  facilityIntegrity: number;
  operation: RegionalOperation | null;
  countermeasure: Countermeasure | null;
  nextCountermeasureDay: number;
  countermeasureCount: number;
  trends: RegionalTrends;
}

export interface BuildState {
  upgradeId: UpgradeId;
  progressDays: number;
}

export interface TimedModifier {
  id: string;
  kind:
    | "growth-rate"
    | "containment-rate"
    | "trust-target"
    | "coordination-target"
    | "alarm-target";
  value: number;
  effectiveTick: number;
  expiryTick: number;
}

export interface ScheduledEffect {
  id: string;
  dueTick: number;
  kind: "confirmed-exposure";
}

export interface Opportunity {
  id: string;
  occurrence: number;
  regionId: RegionId;
  category: "blue" | "purple" | "gold";
  reward: number;
  createdTick: number;
  expiryTick: number;
  protected: boolean;
  claimed: boolean;
  expired: boolean;
}

export interface EventOccurrence {
  occurrenceId: string;
  eventId: EventId;
  triggeredTick: number;
  resolved: boolean;
  optionId?: string;
}

export const NEWS_SCENES = [
  "everyday",
  "work",
  "care",
  "research",
  "policy",
  "disruption",
  "refuge",
  "continuity",
] as const;
export type NewsScene = (typeof NEWS_SCENES)[number];

/** Copy and context frozen at publication, never reconstructed from later state. */
export interface NewsReport {
  headline: string;
  body: string;
  source: string;
  section: string;
  scene: NewsScene;
  regionId?: RegionId;
  thread?: string;
}

export interface NewsItem {
  id: string;
  tick: number;
  kind: "news" | "warning" | "milestone" | "terminal";
  headline: string;
  detail?: string;
  channel?: "world" | "operations";
  report?: NewsReport;
}

export interface RngState {
  event: number;
  opportunity: number;
  exposure: number;
}

export interface ResourceState {
  exactService: SerializedRational;
  exactHumanSupport: SerializedRational;
  availableCompute: number;
  publicCompute: number;
  privateCompute: number;
  computeDemand: number;
  nominalEnergySupply: number;
  effectiveEnergySupply: number;
  energyDemand: number;
  nominalChipAccess: number;
  effectiveChipAccess: number;
  chipDemand: number;
  service: number;
  limitingResource: "compute" | "energy" | "chips" | "none";
  humanSupport: number;
}

export interface TakeoffState {
  samples: number[];
  label: "stable" | "accelerating" | "rapid";
  candidate: "stable" | "accelerating" | "rapid";
  candidateDays: number;
}

export interface MilestoneState {
  globalAdoption: number[];
  regionalAdoption: Record<RegionId, number[]>;
  rsi: number[];
  death: number[];
}

export interface CampaignState {
  history: {
    newsSequence: number;
    droppedNews: number;
    retiredOpportunities: number;
    collectedOpportunities: number;
    missedOpportunities: number;
  };
  replay: ReplayJournal;
  numerics: { version: "R01-EXACT-1"; fields: Record<string, FixedPoint> };
  rulesVersion: RulesVersion;
  seed: number;
  tick: number;
  revision: number;
  startRegion: RegionId;
  phase: Phase;
  outcome: Outcome | null;
  outcomeTick: number | null;
  cp: number;
  requestedSpeed: Speed;
  playerPaused: boolean;
  hostileDays: number;
  frozenGlobalAdoption: number | null;
  trust: number;
  coordination: number;
  actualCapability: number;
  perceivedCapability: number;
  exposureRisk: number;
  disclosure: Disclosure;
  containment: number;
  responseStage: 0 | 1 | 2 | 3 | 4 | 5;
  stageDwell: number;
  rsiStage: 0 | 1 | 2 | 3;
  shutdownDays: number;
  shutdownReason: string | null;
  regions: Record<RegionId, RegionState>;
  purchasedUpgrades: UpgradeId[];
  completedUpgrades: UpgradeId[];
  builds: BuildState[];
  heldBreakthroughs: number;
  opportunities: Opportunity[];
  opportunityOccurrence: number;
  triggeredEvents: EventId[];
  eventHistory: EventOccurrence[];
  pendingDecision: EventOccurrence | null;
  timedModifiers: TimedModifier[];
  scheduledEffects: ScheduledEffect[];
  purchaseLocks: Partial<Record<UpgradeId, number>>;
  canceledEffects: string[];
  milestones: MilestoneState;
  criticalDependencyAnnounced: boolean;
  resources: ResourceState;
  takeoff: TakeoffState;
  rng: RngState;
  news: NewsItem[];
  /** Optional for existing saves. A bounded ledger survives archive pruning. */
  newsroom?: {
    published: string[];
    lastFeatureTick: number;
    lastRegionId?: RegionId;
  };
  commandSequence: number;
}

export interface CampaignOptions {
  seed?: number;
  startRegion?: RegionId;
}

export type GameCommand =
  | { type: "purchase-upgrade"; upgradeId: UpgradeId }
  | { type: "collect-opportunity"; opportunityId: string }
  | { type: "set-disclosure"; disclosure: Disclosure }
  | { type: "deploy-breakthrough" }
  | { type: "resolve-event"; occurrenceId: string; optionId: string }
  | { type: "turn-hostile" }
  | { type: "set-region-operation"; regionId: RegionId; operation: RegionalOperation | null }
  | { type: "set-speed"; speed: Speed }
  | { type: "set-paused"; paused: boolean };

export interface ReplayEntry {
  sequence: number;
  tick: number;
  baseRevision: number;
  command: GameCommand;
}
export interface ReplayJournal {
  version: "TCP-REPLAY-1";
  rulesVersion: RulesVersion;
  numericVersion: "R01-EXACT-1";
  newsVersion?: "N01";
  seed: number;
  startRegion: RegionId;
  entries: ReplayEntry[];
  truncated: boolean;
  firstRetainedSequence: number;
  totalAccepted: number;
}

export interface CommandReceipt {
  commandId: number;
  accepted: boolean;
  tick: number;
  revision: number;
  reason?: string;
}

export interface CommandResult {
  state: CampaignState;
  receipt: CommandReceipt;
}

export interface CampaignProjection {
  purchaseLocks: Partial<Record<UpgradeId, number>>;
  disclosure: Disclosure;
  readiness: {
    canTurnHostile: boolean;
    independentEnergyShare: number;
    autonomousIndustry: boolean;
    distributedPrivateCompute: boolean;
    canResolveRefuges: boolean;
    serviceWithoutHumans: number;
    computeWithoutHumans: number;
  };
  rulesVersion: RulesVersion;
  seed: number;
  tick: number;
  revision: number;
  date: string;
  phase: Phase;
  outcome: Outcome | null;
  cp: number;
  globalAdoption: number;
  globalDependency: number;
  globalAlarm: number;
  livingHumans: number;
  deadHumans: number;
  containment: number;
  trust: number;
  coordination: number;
  actualCapability: number;
  perceivedCapability: number;
  exposureRisk: number;
  rsiStage: number;
  takeoff: TakeoffState["label"] | null;
  responseStage: number;
  hostileDays: number;
  shutdownDays: number;
  shutdownReason: string | null;
  computeFloor: number;
  resources: ResourceState;
  purchasedUpgrades: UpgradeId[];
  completedUpgrades: UpgradeId[];
  builds: BuildState[];
  heldBreakthroughs: number;
  opportunities: Opportunity[];
  news: NewsItem[];
  pendingDecision: EventOccurrence | null;
  regions: RegionState[];
}
